import type { WorkflowState } from "fond/hooks/useCustomWorkflow";

import { BASE_DATA_LAYERS, BaseDataLayerKey, BaseDataPresence, GENERATION_KEYS, GenerationKey, REQUIRED_INPUT_KEYS } from "./layers";
import { Staleness } from "./staleness";

export type BaseDataGatingInput = {
  present: BaseDataPresence;
  staleness: Staleness;
  /** Generation workflows currently starting or in progress. */
  running: readonly GenerationKey[];
  /** Layers with an upload or area select in flight. */
  loading?: readonly BaseDataLayerKey[];
  hasArchitecture: boolean;
  readOnly: boolean;
};

export type Gate = { enabled: true; reason: null } | { enabled: false; reason: string };

export type PanelGenerateButton = Gate & {
  visible: boolean;
  label: "Generate base data" | "Generate again";
};

/** Tools offered by the Modify data flyout. */
export type ModifyDataAction = "editLayers" | "pathPreferences" | "reviewParcels";

export type BaseDataGating = {
  /** Whether each generation workflow can run now, given its dependencies. */
  generate: Record<GenerationKey, Gate>;
  /** Whether each Modify data tool can be used now. */
  modify: Record<ModifyDataAction, Gate>;
  /** What each generation workflow would generate its layer from, given the layers present. */
  generatedFrom: Record<GenerationKey, string>;
  /** Generation workflows that need to run, in the order they run. */
  pending: GenerationKey[];
  panelButton: PanelGenerateButton;
  /** Whether the base data is ready for design. */
  isComplete: boolean;
  requiredInputsAdded: number;
  /** The layer the user should add next, if any. */
  nextKey: BaseDataLayerKey | null;
};

const OPEN: Gate = { enabled: true, reason: null };
const closed = (reason: string): Gate => ({ enabled: false, reason });

/** Return whether a custom workflow is starting or in progress. */
export const isWorkflowRunning = (state: WorkflowState): boolean => state === "STARTING" || state === "PENDING" || state === "IN_PROGRESS";

/** Return the items as an English list, e.g. "a, b and c". */
export const formatList = (items: string[]): string =>
  items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

const commonGate = ({ present, running, readOnly }: BaseDataGatingInput): Gate | null => {
  if (readOnly) return closed("You have read-only access to this version");
  if (running.length > 0) return closed("Base data is being generated");
  const missing = REQUIRED_INPUT_KEYS.filter((key) => !present[key]).map((key) => BASE_DATA_LAYERS[key].name.toLowerCase());
  if (missing.length > 0) return closed(`Add ${formatList(missing)} first`);
  return null;
};

const getModifyGates = (input: BaseDataGatingInput): Record<ModifyDataAction, Gate> => {
  const { present, staleness, running, readOnly } = input;
  const editLayers = readOnly ? closed("You have read-only access to this version") : OPEN;
  const idle = editLayers.enabled && running.length > 0 ? closed("Base data is being generated") : editLayers;
  const pathPreferences = (() => {
    if (!idle.enabled) return idle;
    if (!present.ug) return closed("Add underground path & points first");
    if (staleness.ugStale) return closed("Generate underground path again first");
    return OPEN;
  })();
  return { editLayers: idle, pathPreferences, reviewParcels: commonGate(input) ?? OPEN };
};

const getUgGate = (input: BaseDataGatingInput): Gate => {
  const gate = commonGate(input);
  if (gate) return gate;
  if (!input.hasArchitecture) return closed("Set an architecture first");
  return OPEN;
};

const getLeadinsGate = (input: BaseDataGatingInput): Gate => {
  const gate = commonGate(input);
  if (gate) return gate;
  const { present, staleness } = input;
  if (present.aerial || (present.ug && !staleness.ugStale)) return OPEN;
  if (present.ug) return closed("Generate underground path again first");
  return closed("Add underground path or aerial spans first");
};

const getLeadinsGeneratedFrom = ({ ug, aerial }: BaseDataPresence): string => {
  const sources = ["streets", ...(ug ? ["path", "points"] : []), ...(aerial ? ["spans", "poles"] : [])];
  return `Created from ${formatList(sources)}`;
};

const getNextKey = (present: BaseDataPresence, loading: readonly BaseDataLayerKey[]): BaseDataLayerKey | null => {
  const isAdded = (key: BaseDataLayerKey) => present[key] || loading.includes(key);
  if (!isAdded("boundary") && !REQUIRED_INPUT_KEYS.some(isAdded)) return "boundary";
  return REQUIRED_INPUT_KEYS.find((key) => !isAdded(key)) ?? null;
};

/** Return which base data generation controls are available, what still needs generating, and whether design can begin. */
export const getBaseDataGating = (input: BaseDataGatingInput): BaseDataGating => {
  const { present, staleness, running, loading = [] } = input;
  const generate: Record<GenerationKey, Gate> = { ug: getUgGate(input), leadins: getLeadinsGate(input) };

  const isPending: Record<GenerationKey, boolean> = {
    ug: staleness.ugStale || (!present.ug && !present.aerial),
    leadins: !present.leadins || staleness.leadinsStale,
  };
  const pending = GENERATION_KEYS.filter((key) => isPending[key]);
  const isRegeneration = pending.length > 0 && pending.every((key) => present[key]);

  const requiredInputsAdded = REQUIRED_INPUT_KEYS.filter((key) => present[key]).length;
  const isComplete =
    requiredInputsAdded === REQUIRED_INPUT_KEYS.length &&
    (present.ug || present.aerial) &&
    present.leadins &&
    !staleness.ugStale &&
    !staleness.leadinsStale &&
    running.length === 0;

  return {
    generate,
    modify: getModifyGates(input),
    generatedFrom: { ug: "Created from streets and parcels", leadins: getLeadinsGeneratedFrom(present) },
    pending,
    panelButton: {
      ...(pending.length > 0 ? generate[pending[0]] : OPEN),
      visible: pending.length > 0,
      label: isRegeneration ? "Generate again" : "Generate base data",
    },
    isComplete,
    requiredInputsAdded,
    nextKey: getNextKey(present, loading),
  };
};
